import { RubyGemMark } from '../../components/RubyGemMark';
import { useState } from 'react';
import { Check, FolderPlus, Pencil } from 'lucide-react';
import { FieldLabel } from '@documents-ui';
import type { Folder, FolderColor } from '@platform/shared-types';
import SearchableDropdown from '../../components/SearchableDropdown';
import { documentsApi } from '../../services/documentsApi';
import { toastService } from '../../services/toastService';
import { Button, INPUT_CLASS, ModalShell, folderOptions } from './rubyChrome';
import { FOLDER_COLOR_IDS, folderPalette } from './folderPalette';

/**
 * CREATE OR RENAME A FOLDER, AND GIVE IT A COLOUR.
 *
 * Folders are how an operator files documents — free text, nested at will, and
 * nothing in the product keys on them (that is what `category` is for). So this
 * modal is deliberately cheap: everything on it is safe to change at any time,
 * and none of it touches a version, a signature or where a document is offered.
 *
 * THE COLOUR IS A FILING CUE, NOT A STATUS. It is stored as a palette id rather
 * than a hex value so every surface that draws it can pick its own readable
 * pairing — see ./folderPalette.ts. A folder with no colour reads as neutral,
 * which is exactly how the tree looked before colours existed.
 *
 * THE PARENT PICKER EXCLUDES THE FOLDER ITSELF AND ITS OWN DESCENDANTS. Making
 * a folder its own ancestor would detach that whole branch from the tree — the
 * roots walk would never reach it — and the service does not check, so this
 * does.
 */
export default function FolderEditorModal({
  folder, folders, defaultParentId, onClose, onSaved,
}: {
  /** The folder being edited, or null to create a new one. */
  folder: Folder | null;
  folders: readonly Folder[];
  /** Pre-selected parent when creating — usually the folder in view. */
  defaultParentId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = !!folder;

  const [name, setName] = useState(folder?.name ?? '');
  const [color, setColor] = useState<FolderColor | null>((folder?.color as FolderColor) ?? null);
  const [parentFolderId, setParentFolderId] = useState<string | null>(
    folder ? (folder.parentFolderId ?? null) : (defaultParentId ?? null),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Every folder under `rootId`, itself included — the illegal parents. */
  const descendants = (rootId: string): Set<string> => {
    const out = new Set<string>([rootId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) {
        if (f.parentFolderId && out.has(f.parentFolderId) && !out.has(f.folderId)) {
          out.add(f.folderId);
          grew = true;
        }
      }
    }
    return out;
  };

  const illegal = folder ? descendants(folder.folderId) : new Set<string>();
  const parentChoices = folderOptions(folders.filter((f) => !illegal.has(f.folderId)));

  const nameOk = name.trim().length > 0;

  /**
   * `PATCH /admin/folders/{id}` can SET a parent but has no way to REMOVE one —
   * the body carries fields to set, and an absent key is "leave it alone", not
   * "clear it". Promoting a nested folder back to the top level would therefore
   * save everything else and silently keep the old parent, which is the worst
   * outcome available. Blocked and explained instead.
   */
  const clearingParent = editing && !!folder?.parentFolderId && !parentFolderId;

  const save = async () => {
    if (!nameOk || saving || clearingParent) return;
    setSaving(true);
    setError(null);
    try {
      if (folder) {
        // Only what actually changed — the PATCH route refuses an empty body
        // with `nothing_to_update`, and a no-op write would still stamp
        // `updatedAt` and file an audit entry for a save that changed nothing.
        const patch: Record<string, unknown> = {};
        if (name.trim() !== folder.name) patch.name = name.trim();
        if ((color ?? undefined) !== folder.color && color) patch.color = color;
        if ((parentFolderId ?? undefined) !== folder.parentFolderId && parentFolderId) {
          patch.parentFolderId = parentFolderId;
        }
        if (!Object.keys(patch).length) { onClose(); return; }
        await documentsApi.updateFolder(folder.folderId, patch as any);
        toastService.success(`${name.trim()} saved`);
      } else {
        await documentsApi.createFolder({
          name: name.trim(),
          ...(parentFolderId ? { parentFolderId } : {}),
          ...(color ? { color } : {}),
        });
        toastService.success(`${name.trim()} created`);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.message || (editing ? 'folder_update_failed' : 'folder_create_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={(
        <span className="flex items-center gap-2">
          {editing
            ? <Pencil size={17} className="text-admin-primary" />
            : <FolderPlus size={17} className="text-admin-primary" />}
          {editing ? 'Edit folder' : 'New folder'}
        </span>
      )}
      subtitle={
        editing
          ? 'Renaming, recolouring or re-nesting a folder never moves a document out of it.'
          : 'Folders are shared between Policies and Agreements — one folder holds both kinds.'
      }
      onClose={onClose}
      footerNote="Filing only. Nothing in the product keys on a folder."
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} disabled={!nameOk || saving || clearingParent}>
            {saving
              ? <span className="flex items-center gap-2"><RubyGemMark size={16} drawing color="currentColor" />Saving…</span>
              : (editing ? 'Save folder' : 'Create folder')}
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        <div>
          <FieldLabel requirement="required">Name</FieldLabel>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hardware agreements"
            className={INPUT_CLASS}
            autoFocus
          />
        </div>

        <div>
          <FieldLabel>Colour</FieldLabel>
          <div className="flex flex-wrap items-center gap-2">
            {FOLDER_COLOR_IDS.map((id) => {
              const p = folderPalette(id);
              const on = (color ?? 'stone') === id;
              return (
                <button
                  key={id}
                  type="button"
                  title={p.label}
                  aria-label={p.label}
                  aria-pressed={on}
                  onClick={() => setColor(id)}
                  className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 transition ${
                    on ? 'border-stone-900' : 'border-transparent hover:border-stone-300'
                  }`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${p.swatch}`}>
                    {on && <Check size={14} className="text-white" strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            A filing cue for the tree and the row chip — it means whatever you decide it means.
            No colour reads as neutral.
          </p>
        </div>

        <div>
          <FieldLabel>Parent folder</FieldLabel>
          <SearchableDropdown
            options={[{ value: '__root__', label: 'No parent — top level' }, ...parentChoices]}
            value={parentFolderId ?? '__root__'}
            onChange={(v) => setParentFolderId(!v || v === '__root__' ? null : v)}
            placeholder="No parent — top level"
          />
          {editing && (
            <p className="mt-1.5 text-[11.5px] text-stone-500">
              This folder and everything nested under it are left out of the list — a folder
              cannot be its own ancestor.
            </p>
          )}
          {clearingParent && (
            <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800">
              A parent can be changed but not removed — the service has no way to clear one yet.
              Pick another parent, or leave it where it is.
            </p>
          )}
        </div>

        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
